import {createSlice, PayloadAction,createAsyncThunk} from '@reduxjs/toolkit';
import { api } from '../../services/api';

interface carType{
    id: number;
    makeCompany: string;
    model: string;
    fuelType: string;
    capacity: number;
    mileage: number;
    bootSpace: number;
}
interface FlashDeal {
    id: string;
    dealCode: string;
    dealType: number;
    fromSource: string;
    toDestination: string;
    pickupAddress: string;
    carType: carType;
    pickupStartDatetime: string;
    pickupEndDatetime: string;
    dealStartDatetime: string;
    dealEndDatetime: string;
    basePrice: number;
    offerPrice: number;
    availableSeats: number;
    linkedTripCode: string | null;
    isActive: boolean;
}

interface FlashDealFormData {
  dealCode: string;
  dealType: number;
  fromSource: string;
  toDestination: string;
  pickupAddress: string;
  carType: { id: number };
  pickupStartDatetime: Date | null;
  pickupEndDatetime: Date | null;
  dealStartDatetime: Date | null;
  dealEndDatetime: Date | null;
  basePrice: number | null;
  offerPrice: number | null;
  availableSeats: number | null;
  linkedTripCode: string;
  isActive: boolean;
}

interface FlashDealsState {
  flashDeals: FlashDeal[];
  loading: boolean;
  error: string | null;
  currentFlashDeal: FlashDeal | null;
  formData: FlashDealFormData;
  currentFilter: 'all' | 'active' | 'inactive' | 'currentFlashing';
}

const initialFormData: FlashDealFormData = {
  dealCode: '',
  dealType: 1,
  fromSource: '',
  toDestination: '',
  pickupAddress: '',
  carType: { id: 0 },
  pickupStartDatetime: null,
  pickupEndDatetime: null,
  dealStartDatetime: null,
  dealEndDatetime: null,
  basePrice: null,
  offerPrice: null,
  availableSeats: null,
  linkedTripCode: '',
  isActive: true
};

const initialState: FlashDealsState = {
    flashDeals: [],
    loading: false,
    error: null,
    currentFlashDeal: null,
    formData: initialFormData,
    currentFilter: 'all'
}

export const fetchAllFlashDeals = createAsyncThunk(
    'flashdeals/fetchActiveFlashDeals',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.getAllFlashDeals();
            const typedResponse = response as { status: boolean; message: string; data: FlashDeal[] };
            const activeFlashDealsData: FlashDeal[] = typedResponse?.data || [];
            return activeFlashDealsData;
        } catch (error) {
            console.error('Error fetching flashdeals:', error);
            return rejectWithValue('Failed to fetch flashdeals');
        }
    }
);

export const fetchAllActiveFlashDeals = createAsyncThunk(
    'flashdeals/fetchAllActiveFlashDeals',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.getActiveFlashDeals();
            const typedResponse = response as { status: boolean; message: string; data: FlashDeal[] };
            const activeFlashDealsData: FlashDeal[] = typedResponse?.data || [];
            return activeFlashDealsData;
        } catch (error) {
            console.error('Error fetching active flashdeals:', error);
            return rejectWithValue('Failed to fetch active flashdeals');
        }
    }
);

export const fetchInactiveFlashDeals = createAsyncThunk(
    'flashdeals/fetchInactiveFlashDeals',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.getInActiveFlashDeals();
            const typedResponse = response as { status: boolean; message: string; data: FlashDeal[] };
            const inactiveFlashDealsData: FlashDeal[] = typedResponse?.data || [];
            return inactiveFlashDealsData;
        } catch (error) {
            console.error('Error fetching inactive flashdeals:', error);
            return rejectWithValue('Failed to fetch inactive flashdeals');
        }
    }
);

export const fetchCurrentFlashingDeals = createAsyncThunk(
    'flashdeals/fetchCurrentFlashingDeals',
    async (atDate: string, { rejectWithValue }) => {
        try {
            const response = await api.getcurrentFlasingDeals(atDate);
            const typedResponse = response as { status: boolean; message: string; data: FlashDeal[] };
            const currentFlashingDealsData: FlashDeal[] = typedResponse?.data || [];
            return currentFlashingDealsData;
        } catch (error) {
            console.error('Error fetching current flashing deals:', error);
            return rejectWithValue('Failed to fetch current flashing deals');
        }
    }
);

export const fetchFlashDealByCode = createAsyncThunk(
    'flashdeals/fetchFlashDealByCode',
    async (dealCode: string, { rejectWithValue }) => {
        try {
            const response = await api.getFlashDealByCode(dealCode);
            const typedResponse = response as { status: boolean; message: string; data: FlashDeal };
            return typedResponse?.data || null;
        } catch (error) {
            console.error(`Error fetching flash deal with code ${dealCode}:`, error);
            return rejectWithValue('Failed to fetch flash deal');
        }
    }
);

export const createFlashDeal = createAsyncThunk(
    'flashdeals/createFlashDeal',
    async (flashDealData: any, { rejectWithValue }) => {
        try {
            const response = await api.createFlashDeal(flashDealData);
            const typedResponse = response as { status: boolean; message: string; data: FlashDeal };
            return { data: typedResponse?.data || null, message: typedResponse?.message };
        } catch (error) {
            console.error('Error creating flash deal:', error);
            return rejectWithValue('Failed to create flash deal');
        }
    }
);

export const updateFlashDeal = createAsyncThunk(
    'flashdeals/updateFlashDeal',
    async ({id, flashDealData}: {id: string, flashDealData: any}, { rejectWithValue }) => {   
        try {
            const response = await api.updateFlashDeal(id, flashDealData);
            const typedResponse = response as { status: boolean; message: string; data: FlashDeal };
            return { data: typedResponse?.data || null, message: typedResponse?.message };
        } catch (error) {
            console.error(`Error updating flash deal with code ${id}:`, error);
            return rejectWithValue('Failed to update flash deal');
        } 
    }
);

const flashDealsSlice = createSlice({
  name: 'flashDeals',
  initialState,
  reducers:{
    setFlashDeals(state, action: PayloadAction<FlashDeal[]>) {
        state.flashDeals = action.payload;
        state.error = null;
    },
    setLoading(state, action: PayloadAction<boolean>) {
        state.loading = action.payload;
    },
    setCurrentFlashDeal(state, action: PayloadAction<FlashDeal | null>) {
        state.currentFlashDeal = action.payload;
        state.error = null;
    },
    clearCurrentFlashDeal(state) {
        state.currentFlashDeal = null;
        state.error = null;
    },
    updateFormData(state, action: PayloadAction<Partial<FlashDealFormData>>) {
        state.formData = { ...state.formData, ...action.payload };
    },
    setFormData(state, action: PayloadAction<FlashDealFormData>) {
        state.formData = action.payload;
    },
    clearFormData(state) {
        state.formData = initialFormData;
    },
    setCurrentFilter(state, action: PayloadAction<'all' | 'active' | 'inactive' | 'currentFlashing'>) {
        state.currentFilter = action.payload;
    }
},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllFlashDeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllFlashDeals.fulfilled, (state, action) => {
        state.loading = false;
        state.flashDeals = action.payload;
        state.error = null;
      })
      .addCase(fetchAllFlashDeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAllActiveFlashDeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllActiveFlashDeals.fulfilled, (state, action) => {
        state.loading = false;  
        state.flashDeals = action.payload;
        state.error = null;
      })
      .addCase(fetchAllActiveFlashDeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchInactiveFlashDeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInactiveFlashDeals.fulfilled, (state, action) => {
        state.loading = false;
        state.flashDeals = action.payload;
        state.error = null;
      })
      .addCase(fetchInactiveFlashDeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCurrentFlashingDeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentFlashingDeals.fulfilled, (state, action) => {
        state.loading = false;
        state.flashDeals = action.payload;
        state.error = null;
      })
      .addCase(fetchCurrentFlashingDeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchFlashDealByCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlashDealByCode.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFlashDeal = action.payload;
        state.error = null;
      })
      .addCase(fetchFlashDealByCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createFlashDeal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFlashDeal.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFlashDeal = action.payload?.data || action.payload;
        state.error = null;
      })
      .addCase(createFlashDeal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateFlashDeal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFlashDeal.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFlashDeal = action.payload?.data || action.payload;
        state.error = null;
      })
      .addCase(updateFlashDeal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

  export const  {
    setFlashDeals,
    setLoading,
    setCurrentFlashDeal,
    clearCurrentFlashDeal,
    updateFormData,
    setFormData,
    clearFormData,
    setCurrentFilter
  } =  flashDealsSlice.actions;

  export default flashDealsSlice.reducer;